import { Controller } from "@hotwired/stimulus"
import { Modal, Toast } from "bootstrap"

export default class extends Controller {
  static targets = ["selectAll", "checkbox", "counter", "submitBtn", "search", "propertyFilter", "countryFilter", "row"]
  static values = { url: String, csrf: String }

  connect() {
    this.element.addEventListener('shown.bs.modal', () => this.resetFilters())
  }

  resetFilters() {
    if (this.hasSearchTarget) this.searchTarget.value = ''
    if (this.hasPropertyFilterTarget) this.propertyFilterTarget.value = ''
    if (this.hasCountryFilterTarget) this.countryFilterTarget.value = ''
    this.rowTargets.forEach((row) => { row.style.display = '' })
    this.updateSelectAllState()
  }

  filter() {
    const search = this.hasSearchTarget ? this.searchTarget.value.toLowerCase() : ''
    const property = this.hasPropertyFilterTarget ? this.propertyFilterTarget.value : ''
    const country = this.hasCountryFilterTarget ? this.countryFilterTarget.value : ''

    this.rowTargets.forEach((row) => {
      const matchName = !search || row.dataset.assetName.includes(search)
      const matchProperty = !property || row.dataset.propertyKind === property
      const matchCountry = !country || row.dataset.country === country
      row.style.display = (matchName && matchProperty && matchCountry) ? '' : 'none'
    })

    this.updateSelectAllState()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.visibleCheckboxes().forEach((cb) => { cb.checked = checked })
    this.updateCounter()
  }

  updateCounter() {
    const count = this.selectedIds().length
    this.counterTarget.textContent = `${count} valuation${count === 1 ? '' : 's'} selected`
    this.submitBtnTarget.disabled = count === 0
    this.updateSelectAllState()
  }

  updateSelectAllState() {
    const visible = this.visibleCheckboxes()
    if (visible.length === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
      return
    }
    const checkedCount = visible.filter((cb) => cb.checked).length
    this.selectAllTarget.checked = checkedCount === visible.length
    this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < visible.length
  }

  visibleCheckboxes() {
    return this.checkboxTargets.filter((cb) => {
      const row = cb.closest('tr')
      return row && row.style.display !== 'none'
    })
  }

  async push() {
    const ids = this.selectedIds()
    if (ids.length === 0) return

    // EVA-147: the period growth-rates grid (React) exposes which rate rows
    // are ticked. Send only those; if the bridge is absent the backend pushes
    // everything (backward compatible).
    const rateIds = typeof window.__evaSelectedGrowthRateIds === 'function'
      ? window.__evaSelectedGrowthRateIds()
      : undefined
    if (Array.isArray(rateIds) && rateIds.length === 0) {
      this.showModalError('Select at least one rate to push (tick the rows in the table).')
      return
    }

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Pushing...'

    try {
      const body = { valuation_ids: ids }
      if (Array.isArray(rateIds)) body.growth_rate_ids = rateIds

      const res = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify(body)
      })

      const json = await res.json()

      if (json.success) {
        this.closeModal()
        this.showToast(`Successfully pushed assumptions to ${json.count} valuation(s)`)
      } else if (json.error === 'no_rates_selected') {
        this.showModalError('Select at least one rate to push (tick the rows in the table).')
      } else {
        this.showModalError(json.error || 'An error occurred')
      }
    } catch (e) {
      this.showModalError('Network error. Please try again.')
    } finally {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.innerHTML = '<i class="bi bi-box-arrow-right"></i> Push to Valuations'
    }
  }

  selectedIds() {
    return this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => parseInt(cb.value, 10))
  }

  closeModal() {
    const modalEl = this.element
    const modal = Modal.getInstance(modalEl)
    if (modal) modal.hide()
  }

  showToast(message) {
    const container = document.querySelector('.toast-container') || this.createToastContainer()
    const toastEl = document.createElement('div')
    toastEl.className = 'toast align-items-center text-bg-success border-0'
    toastEl.setAttribute('role', 'alert')
    toastEl.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
      </div>
    `
    container.appendChild(toastEl)
    const toast = new Toast(toastEl, { delay: 5000 })
    toast.show()
    toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove())
  }

  showModalError(message) {
    let errorEl = this.element.querySelector('.push-error-alert')
    if (!errorEl) {
      errorEl = document.createElement('div')
      errorEl.className = 'alert alert-danger push-error-alert mt-2 mb-0'
      this.element.querySelector('.modal-body').appendChild(errorEl)
    }
    errorEl.textContent = message
  }

  createToastContainer() {
    const container = document.createElement('div')
    container.className = 'toast-container position-fixed bottom-0 end-0 p-3'
    container.style.zIndex = '1090'
    document.body.appendChild(container)
    return container
  }
}
